/**
 * The visible rules of a word ladder, mirrored from `backend/apps/puzzletypes/word_ladder/
 * ladder.py`. The dictionary is not here and never will be: whether a rung is a word is only
 * known to the server, at submission.
 */
import type { IssueCode } from "./types";

export interface Issue {
  code: IssueCode;
  /** Position in the full ladder (the start word is 0), or null for the ladder as a whole. */
  index: number | null;
  /** The player's own rung, never a rung of the author's ladder. */
  word?: string;
  params?: Record<string, number>;
}

/** Mirrors the server's `normalize_word`: accents removed, surrounding spaces trimmed, uppercase. */
export function normalizeWord(word: string): string {
  return word
    .trim()
    .normalize("NFD")
    .replace(/\p{Mn}/gu, "")
    .normalize("NFC")
    .toUpperCase();
}

/** Letters that differ between two words of the same length. */
export function distance(a: string, b: string): number {
  let count = 0;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) count += 1;
  return count;
}

export function oneLetterApart(a: string, b: string): boolean {
  return a.length === b.length && distance(a, b) === 1;
}

/** A word of `length` ASCII letters. */
export function shaped(word: string, length: number): boolean {
  return word.length === length && /^[A-Z]+$/.test(word);
}

/** Positions where `word` differs from `previous`, for the eye; empty when the shapes differ. */
export function changedPositions(previous: string, word: string): number[] {
  if (previous.length !== word.length) return [];
  return [...word].flatMap((ch, i) => (ch === previous[i] ? [] : [i]));
}

/**
 * Everything wrong with `ladder` (normalised) by the rules a player can see, rung by rung,
 * in the order the server lists them. The server adds `unknown_word` on top.
 */
export function ladderIssues(
  start: string,
  end: string,
  maxSteps: number | null | undefined,
  ladder: string[],
): Issue[] {
  const issues: Issue[] = [];
  const length = start.length;
  if (ladder.length === 0) return [{ code: "wrong_start", index: 0, word: "" }];
  if (ladder[0] !== start) issues.push({ code: "wrong_start", index: 0, word: ladder[0] });
  const last = ladder.length - 1;
  for (let index = 1; index < ladder.length; index += 1) {
    const word = ladder[index];
    const previous = ladder[index - 1];
    if (index === last && word !== end) {
      issues.push({ code: "wrong_end", index, word });
      continue;
    }
    if (!shaped(word, length)) {
      issues.push({ code: "wrong_length", index, word });
      continue;
    }
    if (shaped(previous, length) && !oneLetterApart(previous, word))
      issues.push({ code: "not_one_letter", index, word });
  }
  if (last === 0 && ladder[0] !== end)
    issues.push({ code: "wrong_end", index: 0, word: ladder[0] });
  if (maxSteps !== null && maxSteps !== undefined && last > maxSteps)
    issues.push({ code: "too_many_steps", index: null, params: { steps: last, max: maxSteps } });
  return issues;
}

/**
 * The live reading of a ladder being climbed: the rungs so far, between the start word and
 * the end word, which is not yet there. Silent unless a rung is certainly wrong (§16.4):
 * a rung of the wrong length, a step that does not change exactly one letter, or a ladder
 * that can no longer reach the end within `maxSteps` — each remaining step changes one
 * letter, so at least as many steps as letters still differ from the end word.
 */
export function partialIssues(
  start: string,
  end: string,
  maxSteps: number | null | undefined,
  rungs: string[],
): Issue[] {
  const issues: Issue[] = [];
  const length = start.length;
  rungs.forEach((word, i) => {
    const previous = i === 0 ? start : rungs[i - 1];
    if (!shaped(word, length)) issues.push({ code: "wrong_length", index: i + 1, word });
    else if (shaped(previous, length) && !oneLetterApart(previous, word))
      issues.push({ code: "not_one_letter", index: i + 1, word });
  });
  const last = rungs.length > 0 ? rungs[rungs.length - 1] : start;
  if (maxSteps !== null && maxSteps !== undefined && shaped(last, length)) {
    const least = rungs.length + distance(last, end);
    if (least > maxSteps)
      issues.push({ code: "too_many_steps", index: null, params: { steps: least, max: maxSteps } });
  }
  return issues;
}
